/**
 * Price Line Item Component
 * Displays a single pricing line with label, amount, and optional tooltip
 */

import { Tooltip } from './Tooltip';
import { formatCurrency } from '../../utils/formatCurrency';

interface PriceLineItemProps {
  label: string;
  amount?: number | string;
  tooltip?: string;
  isTotal?: boolean;
  isSubtotal?: boolean;
  showAmount?: boolean; // Default true, set false to hide amount (for "Included" etc)
  customAmount?: string; // For custom text like "Included", "Per ton", etc
}

/**
 * PriceLineItem component
 * Shows a single line in a pricing breakdown
 */
export function PriceLineItem({
  label,
  amount,
  tooltip,
  isTotal = false,
  isSubtotal = false,
  showAmount = true,
  customAmount,
}: PriceLineItemProps) {
  // Format amount
  const formattedAmount = customAmount
    ? customAmount
    : amount !== undefined
    ? typeof amount === 'number'
      ? formatCurrency(amount)
      : amount
    : '';

  // Determine text size and weight based on line type
  const labelClasses = isTotal
    ? 'text-base font-semibold text-gray-900'
    : isSubtotal
    ? 'text-sm font-medium text-gray-900'
    : 'text-sm text-gray-600';

  const amountClasses = isTotal
    ? 'text-xl font-bold text-primary-600 tabular-nums'
    : isSubtotal
    ? 'text-sm font-semibold text-gray-900 tabular-nums'
    : 'text-sm font-medium text-gray-900 tabular-nums';

  return (
    <div
      className={`flex items-center justify-between ${
        isTotal ? 'py-1' : ''
      }`}
    >
      {/* Label with optional tooltip */}
      <div className="flex items-center gap-1.5">
        <span className={labelClasses}>{label}</span>
        {tooltip && <Tooltip content={tooltip} />}
      </div>

      {/* Amount */}
      {showAmount && <span className={amountClasses}>{formattedAmount}</span>}
    </div>
  );
}

/**
 * Divider component for pricing breakdown sections
 */
export function PricingDivider() {
  return <div className="border-t border-gray-300 my-3" />;
}
